/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import dayjs from 'dayjs';
import {useStores} from "../../../stores";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";

// noinspection ES6UnusedImports
import relativeTime from 'dayjs/plugin/relativeTime';
import {AdminPanelSettings, Bathtub, DeleteForever, PlayArrow, Publish, ThumbDown} from "@mui/icons-material";
import {UNCATEGORIZED_LABEL} from "../../../constants";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import {UpdateCutoffData} from "../../../stores/categorization-review/AiCategorizationJobDelegate";
import {DevopsCreateCategorizationApprovalStats} from "../../../services/classes/AiClasses";
import {rollbar} from "../../../env";

export const RevisionSelectorDropdown = observer(() => {
    const {categorizationReviewStore, authStore, materializedApi} = useStores();

    const approvalController = categorizationReviewStore.reviewPageController.reviewPageApprovalController;
    const aiJobDelegate = categorizationReviewStore.aiJobDelegate;
    const revisionController = categorizationReviewStore.reviewPageController.reviewPageRevisionController;
    const statusDelegate = categorizationReviewStore.statusDelegate;
    const revisionId = typeof revisionController.revision === 'number' ? revisionController.revision : undefined;

    const [isPublishConfirmationOpen, setIsPublishConfirmationOpen] = React.useState(false);
    const [isRejectConfirmationOpen, setIsRejectConfirmationOpen] = React.useState(false);
    const [isDestroyConfirmationOpen, setIsDestroyConfirmationOpen] = React.useState(false);
    const [isApplyCutoffConfirmationOpen, setIsApplyCutoffConfirmationOpen] = React.useState(false);

    const [cutoffValue, setCutoffValue] = React.useState<string>(String(.5));
    const cutoffValueFloat = parseFloat(cutoffValue.replace(',', '.'));
    const cutoffValueValid = cutoffValueFloat >= 0 && cutoffValueFloat <= 1;

    const [cutoffValueL2, setCutoffValueL2] = React.useState<string>(String(0));
    const cutoffValueL2Float = parseFloat(cutoffValueL2.replace(',', '.'));
    const cutoffValueL2Valid = cutoffValueL2Float >= 0 && cutoffValueL2Float <= 1;

    const [cutoffValueL3, setCutoffValueL3] = React.useState<string>(String(0));
    const cutoffValueL3Float = parseFloat(cutoffValueL3.replace(',', '.'));
    const cutoffValueL3Valid = cutoffValueL3Float >= 0 && cutoffValueL3Float <= 1;

    const [cutoffValueL4, setCutoffValueL4] = React.useState<string>(String(0));
    const cutoffValueL4Float = parseFloat(cutoffValueL4.replace(',', '.'));
    const cutoffValueL4Valid = cutoffValueL4Float >= 0 && cutoffValueL4Float <= 1;

    const job = statusDelegate.latestCategorizationJob;
    const categorizationJobId = categorizationReviewStore.statusDelegate.categorizationJobId;

    // /approve/category/devops_create_for_everyone/
    const [isSendForAllOpen, setIsSendForAllOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [approvalStats, setApprovalStats] = React.useState<DevopsCreateCategorizationApprovalStats | undefined>(undefined);
    useEffect(() => {
        if (isLoading) return;
        if (!isSendForAllOpen) {
            if (approvalStats) {
                setApprovalStats(undefined);
            }
            return;
        }
        if (approvalStats) return;
        if (!categorizationReviewStore.bagId || categorizationReviewStore.bagId <= 0) {
            console.warn('No bagId, cannot get approval stats')
        }
        setIsLoading(true)
        materializedApi.devopsCreateCategorizationApprovalRequestForEveryoneStatistics(categorizationReviewStore.bagId)
            .then(r => setApprovalStats(r.data))
            .finally(() => setIsLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorizationReviewStore.bagId, isLoading, approvalStats, isSendForAllOpen]);


    useEffect(() => {
        if (authStore.isMithraStaffGroup) {
            revisionController._revisionRequestManager.request()
        }
    }, [authStore.isMithraStaffGroup, revisionController, categorizationReviewStore.statusDelegate.categorizationJobStatus])

    if (!authStore.isMithraStaffGroup) return <></>;

    return <Grid container alignItems="center" spacing={2}>
        <Dialog open={isPublishConfirmationOpen} onClose={() => setIsPublishConfirmationOpen(false)}>
            <DialogTitle>
                Are you sure you want to publish this revision?
            </DialogTitle>
            {/*<DialogContent>*/}
            {/*</DialogContent>*/}
            <DialogActions>
                <Button onClick={() => setIsPublishConfirmationOpen(false)}>
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (categorizationJobId) aiJobDelegate.devopsGoLiveRequestManager.request(categorizationJobId);
                        setIsPublishConfirmationOpen(false);
                    }}>
                    Publish
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={isRejectConfirmationOpen} onClose={() => setIsRejectConfirmationOpen(false)}>
            <DialogTitle>
                Are you sure you want to reject this revision?
            </DialogTitle>
            {/*<DialogContent>*/}
            {/*</DialogContent>*/}
            <DialogActions>
                <Button onClick={() => setIsPublishConfirmationOpen(false)}>
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (categorizationJobId) aiJobDelegate.devopsRejectRevisionManager.request(categorizationJobId);
                        setIsPublishConfirmationOpen(false);
                    }}>
                    Reject
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={isDestroyConfirmationOpen} onClose={() => setIsDestroyConfirmationOpen(false)}>
            <DialogTitle>
                Are you sure you want to destroy this job?
            </DialogTitle>
            {/*<DialogContent>*/}
            {/*</DialogContent>*/}
            <DialogActions>
                <Button onClick={() => setIsDestroyConfirmationOpen(false)}>
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (categorizationJobId) aiJobDelegate.devopsDestroyJobManager.request(categorizationJobId);
                        setIsDestroyConfirmationOpen(false);
                    }}>
                    Destroy
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={isApplyCutoffConfirmationOpen} onClose={() => setIsApplyCutoffConfirmationOpen(false)}>
            <DialogTitle>
                Apply a cutoff value
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" style={{marginBottom: '1em'}}>
                    Everything below this score will be set to {UNCATEGORIZED_LABEL}
                </Typography>

                <TextField
                    style={{marginBottom: '3em'}}
                    error={!cutoffValueValid}
                    label="Cutoff value"
                    helperText={!cutoffValueValid ? 'Value must be between 0 and 1' : ''}
                    value={cutoffValue}
                    onChange={(e) => setCutoffValue(e.target.value)}
                    onBlur={() => cutoffValueValid && setCutoffValue(String(cutoffValueFloat))}
                /><br/>

                <TextField
                    style={{marginBottom: '1em'}}
                    error={!cutoffValueL2Valid}
                    label="Cutoff value (L2)"
                    helperText={!cutoffValueL2Valid ? 'Value must be between 0 and 1' : 'Leave at 0 to ignore'}
                    value={cutoffValueL2}
                    onChange={(e) => setCutoffValueL2(e.target.value)}
                    onBlur={() => cutoffValueL2Valid && setCutoffValueL2(String(cutoffValueL2Float))}
                /><br/>
                <TextField
                    style={{marginBottom: '1em'}}
                    error={!cutoffValueL3Valid}
                    label="Cutoff value (L3)"
                    helperText={!cutoffValueL3Valid ? 'Value must be between 0 and 1' : 'Leave at 0 to ignore'}
                    value={cutoffValueL3}
                    onChange={(e) => setCutoffValueL3(e.target.value)}
                    onBlur={() => cutoffValueL3Valid && setCutoffValueL3(String(cutoffValueL3Float))}
                /><br/>
                <TextField
                    style={{marginBottom: '1em'}}
                    error={!cutoffValueL4Valid}
                    label="Cutoff value (L4)"
                    helperText={!cutoffValueL4Valid ? 'Value must be between 0 and 1' : 'Leave at 0 to ignore'}
                    value={cutoffValueL4}
                    onChange={(e) => setCutoffValueL4(e.target.value)}
                    onBlur={() => cutoffValueL4Valid && setCutoffValueL4(String(cutoffValueL4Float))}
                /><br/>

                <Button
                    variant="outlined"
                    className="btn-with-circular-progress"
                    startIcon={aiJobDelegate.devopsSimulateCutoffManager.busy
                        ? <CircularProgress size={14}/>
                        : <PlayArrow/>}
                    disabled={!cutoffValueValid || !revisionId || !isApplyCutoffConfirmationOpen}
                    onClick={() => {
                        if (cutoffValueValid && revisionId && isApplyCutoffConfirmationOpen) {
                            aiJobDelegate.devopsSimulateCutoffManager.request({
                                cutoff_value: cutoffValueFloat,
                                revision: revisionId
                            });
                        }
                    }}>
                    Simulate {UNCATEGORIZED_LABEL} (Only for L1)
                </Button>
                {aiJobDelegate.devopsSimulateCutoffManager.error && <Alert color="error">
                    {'' + aiJobDelegate.devopsSimulateCutoffManager.error}
                </Alert>}

                <List style={{opacity: aiJobDelegate.devopsSimulateCutoffManager.busy ? '.25' : '1'}}>
                    <ListItem disableGutters>
                        <ListItemText
                            primary={UNCATEGORIZED_LABEL + ` spend`}
                            secondary={aiJobDelegate.devopsSimulateCutoffManager.result?.spend &&
                                <CurrencyComponent v={aiJobDelegate.devopsSimulateCutoffManager.result?.spend}/>}
                        />
                        <ListItemText
                            primary={UNCATEGORIZED_LABEL + ` parts`}
                            secondary={aiJobDelegate.devopsSimulateCutoffManager.result?.parts}
                        />
                        <ListItemText
                            primary={`Suppliers with ${UNCATEGORIZED_LABEL} parts`}
                            secondary={aiJobDelegate.devopsSimulateCutoffManager.result?.suppliers}
                        />
                    </ListItem>
                </List>
                {!revisionId && <Alert color="error">
                    Please select a revision first
                </Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsApplyCutoffConfirmationOpen(false)}>
                    Cancel
                </Button>
                <Button
                    disabled={!categorizationJobId || !revisionId || !cutoffValueValid}
                    onClick={() => {
                        if (categorizationJobId && revisionId && cutoffValueValid) {
                            const data: UpdateCutoffData = {
                                cutoff_value: cutoffValueFloat,
                                cutoff_value_l2: cutoffValueL2Float,
                                cutoff_value_l3: cutoffValueL3Float,
                                cutoff_value_l4: cutoffValueL4Float,
                                revision: revisionId,
                            };
                            aiJobDelegate.devopsApplyCutoffManager.request([categorizationJobId, data]);
                        }
                        setIsApplyCutoffConfirmationOpen(false);
                    }}>
                    Apply Cutoff
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={isSendForAllOpen} onClose={() => setIsSendForAllOpen(false)}>
            <DialogTitle>
                Send for approval for everyone
            </DialogTitle>
            <DialogContent>
                <Typography>
                    Create an approval request for all reviews that are currently pending, for all users.<br/>
                    Note: This must not be used during a revision!<br/>
                </Typography>
                {isLoading
                    ? <p>Loading statistics</p>
                    : approvalStats ? <pre>{JSON.stringify(approvalStats, null, 1)}</pre>
                        : <p></p>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsSendForAllOpen(false)}>
                    Cancel
                </Button>
                <Button
                    variant="outlined"
                    disabled={approvalController.isApprovalJobRunning || approvalController.isBusySendForApproval}
                    startIcon={<AdminPanelSettings/>}
                    onClick={() => {
                        approvalController.devopsSendForApprovalEveryone(
                            () => {
                                setIsSendForAllOpen(false)

                                // Reload the page when the approval is send
                                categorizationReviewStore.reviewPageController.loadData(true)
                            },
                            (err) => rollbar.error(err),
                        )
                    }}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>

        <Grid item>
            <Button
                variant="text"
                startIcon={<>
                    {statusDelegate.statusRequestManager.busy ? <CircularProgress size={14}/> : null}
                    <AdminPanelSettings/>
                </>}
                onClick={() => {
                    statusDelegate.checkStatus();
                }}>
                Job Status: {job ? `${job?.current_status.status} (${job?.current_status.timestamp})` : 'No job'}
                {statusDelegate.statusRequestManager.error ? ' (Error)' : ''}
            </Button>

            <Button
                // color=""
                variant="outlined"
                className="btn-with-circular-progress"
                startIcon={aiJobDelegate.devopsDestroyJobManager.busy
                    ? <CircularProgress size={14}/>
                    : <DeleteForever/>}
                disabled={aiJobDelegate.devopsDestroyJobManager.busy || !categorizationJobId}
                onClick={() => setIsDestroyConfirmationOpen(true)}>
                Destroy
                {aiJobDelegate.devopsDestroyJobManager.error && ' (Error)'}
            </Button>
        </Grid>

        <Grid item>
            <FormControl style={{minWidth: '10em'}}>
                <InputLabel id="revision-selector-dropdown-label">Revision</InputLabel>
                <Select
                    label="Revision"
                    labelId="revision-selector-dropdown-label"
                    color={revisionController.revision === 'customer' ? 'warning' : undefined}
                    value={revisionController.revision || ''}
                    onChange={(e) => revisionController.setRevision(String(e.target.value))}>
                    <MenuItem value="">
                        Disable
                    </MenuItem>
                    <MenuItem value="customer" color="warning">
                        Customer version
                    </MenuItem>
                    {revisionController.revisions?.filter(r => r.databag === categorizationReviewStore.bagId).map(r =>
                        <MenuItem key={r.id} value={r.id}>
                            <ListItemText
                                primary={`Revision ${r.id}`}
                                secondary={<ul>
                                    <li>Created: {dayjs(r.created).fromNow()}</li>
                                    {r.notes && <li>Notes: {r.notes}</li>}
                                    {r.rejected && <li>Rejected: {'' + r.rejected}</li>}
                                </ul>}
                            />
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </Grid>
        <Grid item>
            {revisionController.revision === 'customer' &&
                <Alert color="warning">
                    You are modifying the customer's view
                </Alert>
            }
            {Number.isInteger(revisionController.revision) &&
                <Alert
                    color={categorizationReviewStore.statusDelegate.latestRevisionIsRejected ? 'error' : 'info'}
                    action={categorizationReviewStore.statusDelegate.latestRevisionIsRejected
                        ? null
                        : <>
                            {revisionId && <Button
                                variant="outlined"
                                startIcon={aiJobDelegate.devopsApplyCutoffManager.busy
                                    ? <CircularProgress size={14}/>
                                    : <Bathtub/>}
                                disabled={aiJobDelegate.devopsApplyCutoffManager.busy}
                                onClick={() => setIsApplyCutoffConfirmationOpen(true)}>
                                Cutoff
                                {aiJobDelegate.devopsApplyCutoffManager.error && ' (Error)'}
                            </Button>}
                            {categorizationReviewStore.statusDelegate.latestRevisionIsNotRejected && <Button
                                // color=""
                                variant="outlined"
                                className="btn-with-circular-progress"
                                // style={{marginLeft: '2em'}}
                                startIcon={aiJobDelegate.devopsRejectRevisionManager.busy
                                    ? <CircularProgress size={14}/>
                                    : <ThumbDown/>}
                                disabled={aiJobDelegate.devopsRejectRevisionManager.busy}
                                onClick={() => setIsRejectConfirmationOpen(true)}>
                                Reject
                                {aiJobDelegate.devopsRejectRevisionManager.error && ' (Error)'}
                            </Button>}
                            {revisionController.revision === categorizationReviewStore.statusDelegate.latestRevisionId &&
                                <Button
                                    // color=""
                                    variant="outlined"
                                    className="btn-with-circular-progress"
                                    // style={{marginLeft: '2em'}}
                                    startIcon={aiJobDelegate.devopsGoLiveRequestManager.busy
                                        ? <CircularProgress size={14}/>
                                        : <Publish/>}
                                    disabled={aiJobDelegate.devopsGoLiveRequestManager.busy}
                                    onClick={() => setIsPublishConfirmationOpen(true)}>
                                    Publish
                                    {aiJobDelegate.devopsGoLiveRequestManager.error && ' (Error)'}
                                </Button>
                            }
                        </>
                    }>
                    {categorizationReviewStore.statusDelegate.latestRevisionIsRejected
                        ? 'This revision is already rejected'
                        : 'You are modifying a revision'}
                </Alert>
            }
        </Grid>
        <Grid item>
            <Button
                variant="outlined"
                startIcon={<AdminPanelSettings/>}
                onClick={() => setIsSendForAllOpen(true)}>
                Send for everyone
            </Button>
        </Grid>
    </Grid>
});